import React, { ReactElement, useMemo } from 'react';
import Table, {
    createDetailLinkTh,
    createTd,
    createTh,
} from 'components/tables/Table';
import {
    ColumnHeadInterface,
    ColumnSize,
    ColumnValueInterface,
    ColumnValueWeight,
} from 'components/interfaces/GeneralInterface';
import { useTranslation } from 'utils/localization';
import { toDate } from 'utils/helpers/datetime';
import { CoachClubListItemApiInterface } from 'submodules/api_middleware';
import { FILTER_PREFIX } from 'submodules/api_middleware/src/client';
import { getRoute } from 'constants/routes';
import { MATCH_FILTER_KEYS } from 'constants/filterKeysConstants';
import styles from 'components/pages/coach/detail/CoachCareerBlock.module.scss';

interface Props {
    entries: CoachClubListItemApiInterface[];
    title: string;
    /** Coach publish id — used to deep-link the row magnifier into Utkání. */
    coachId: string;
}

interface CareerTotals {
    matchesTotal: number;
    matchesWon: number;
    matchesDrawn: number;
    matchesLost: number;
    cardsYellow: number;
    cardsRed: number;
}

/**
 * Coach career block — the IS1 `Is1TrenerOddil` table flattened into a small
 * Passport-style card. Lives inline on the Osobní údaje page below
 * `CoachLicencesBlock`. Each row carries per-stint stats (matches W/D/L,
 * cards) computed by the BE inside the stint window, and a magnifying-glass
 * column that deep-links into Utkání pre-filtered by that club via the
 * combined `team_internal_id` filter.
 */
const CoachCareerBlock = ({ entries, title, coachId }: Props): ReactElement => {
    const { t } = useTranslation();

    const columns: ColumnHeadInterface[] = useMemo(
        () => [
            // Magnifier deep-link into Utkání. We always return *some* path so
            // the icon renders for every row — `ColumnContent` hides the icon
            // when the path is undefined, which would silently swallow the
            // column. The Filters component reads pre-set values from the URL
            // under the `fi_` prefix, so the deep-link must use the prefixed
            // key (e.g. `fi_team_internal_id=…`); the bare key would land in
            // the URL but the Filters wouldn't pick it up. If the BE doesn't
            // carry `club_internal_id` (older deploy of the coach-clubs
            // endpoint, or a club row without `Is1Oddil.IdOddil`) we still
            // let the user jump to the generic Utkání list — the team filter
            // is just dropped.
            createDetailLinkTh((id) => {
                const internalId = String(id ?? '').trim();
                if (!internalId) {
                    return getRoute('/coaches/{0}/matches', [coachId]);
                }
                return getRoute('/coaches/{0}/matches', [coachId], undefined, {
                    [`${FILTER_PREFIX}${MATCH_FILTER_KEYS.team_internal_id}`]:
                        internalId,
                });
            }),
            createTh(t, 'CoachPage.clubs.club', ColumnSize.M),
            createTh(t, 'CoachPage.clubs.validFrom', ColumnSize.S),
            createTh(t, 'CoachPage.clubs.validTo', ColumnSize.S),
            createTh(t, 'CoachPage.clubs.matchesTotal', ColumnSize.S),
            createTh(t, 'CoachPage.clubs.matchesWon', ColumnSize.S),
            createTh(t, 'CoachPage.clubs.matchesDrawn', ColumnSize.S),
            createTh(t, 'CoachPage.clubs.matchesLost', ColumnSize.S),
            createTh(t, 'CoachPage.clubs.cards', ColumnSize.S),
        ],
        [t, coachId]
    );

    const data: ColumnValueInterface[][] = useMemo(
        () =>
            (entries ?? []).map((row) => [
                createTd(row.club_internal_id ?? ''),
                createTd(row.club_name || '–', ColumnValueWeight.BOLD),
                createTd(toDate(row.valid_from)),
                createTd(toDate(row.valid_to)),
                createTd(String(row.matches_total ?? 0)),
                createTd(String(row.matches_won ?? 0)),
                createTd(String(row.matches_drawn ?? 0)),
                createTd(String(row.matches_lost ?? 0)),
                createTd(
                    String((row.cards_yellow ?? 0) + (row.cards_red ?? 0))
                ),
            ]),
        [entries]
    );

    const totals: CareerTotals = useMemo(() => {
        const acc: CareerTotals = {
            matchesTotal: 0,
            matchesWon: 0,
            matchesDrawn: 0,
            matchesLost: 0,
            cardsYellow: 0,
            cardsRed: 0,
        };
        for (const row of entries ?? []) {
            acc.matchesTotal += row.matches_total ?? 0;
            acc.matchesWon += row.matches_won ?? 0;
            acc.matchesDrawn += row.matches_drawn ?? 0;
            acc.matchesLost += row.matches_lost ?? 0;
            acc.cardsYellow += row.cards_yellow ?? 0;
            acc.cardsRed += row.cards_red ?? 0;
        }
        return acc;
    }, [entries]);

    const summaryCells: { labelKey: string; value: number }[] = [
        {
            labelKey: 'CoachPage.clubs.summary.matchesTotal',
            value: totals.matchesTotal,
        },
        {
            labelKey: 'CoachPage.clubs.summary.matchesWon',
            value: totals.matchesWon,
        },
        {
            labelKey: 'CoachPage.clubs.summary.matchesDrawn',
            value: totals.matchesDrawn,
        },
        {
            labelKey: 'CoachPage.clubs.summary.matchesLost',
            value: totals.matchesLost,
        },
        {
            labelKey: 'CoachPage.clubs.summary.cardsYellow',
            value: totals.cardsYellow,
        },
        {
            labelKey: 'CoachPage.clubs.summary.cardsRed',
            value: totals.cardsRed,
        },
    ];

    return (
        <>
            <Table
                columns={columns}
                data={data}
                cardTitle={title}
                noEntriesSubText={t('CoachPage.clubs.empty')}
            />
            {(entries?.length ?? 0) > 0 && (
                <div className={styles.summary}>
                    {summaryCells.map(({ labelKey, value }) => (
                        <div key={labelKey} className={styles.cell}>
                            <span className={styles.label}>{t(labelKey)}</span>
                            <span className={styles.value}>{value}</span>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default CoachCareerBlock;
